function clearSearchParams(searchParams) {
  searchParams.delete("q");
  searchParams.delete("category");
  searchParams.delete("location");
  searchParams.delete("state");
}

// clear the search results, if the results are being cleared because
// of a change to history, don't update the URL
function clearResults(updateUrl = true) {
  const listingsContainer = document.getElementById("js_map_search_results");
  if (listingsContainer) {
    listingsContainer.innerHTML = "";
  }

  const mapFilterInput = document.getElementById("js_search_page_filter");
  if (mapFilterInput) {
    mapFilterInput.value = "";
  }

  const clearOrBrowseAnchor = document.getElementById("js_clear_or_browse");
  if (clearOrBrowseAnchor) {
    clearOrBrowseAnchor.classList.remove("show");
  }

  if (updateUrl && 'URLSearchParams' in window) { // doesn't work in IE
    const searchParams = new URLSearchParams(window.location.search);
    clearSearchParams(searchParams);

    const nonSearchRelatedParams = searchParams.toString();
    const nextPath = nonSearchRelatedParams.length
      ? `${window.location.pathname}?${nonSearchRelatedParams}`
      : window.location.pathname;

    window.history.pushState(null, '', nextPath);
  }
}

function generateSrcSetString(photos) {
  return photos.map(photo => `${window.rambleMaps.imageUrl}${photo.name} ${photo.width}w`).join(",");
}

// check if two array contents match
function arraysMatch(arr1, arr2) {
  // Check if the arrays are the same length
  if (arr1.length !== arr2.length) return false;

  // Check if all items exist and are in the same order
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }

  // Otherwise, return true
  return true;
}

// Determine the proper value for the sizes attribute of the img for the srcset
// These ratios need to match the values in templates/listing.html
function calculateSizes(ratio) {
  /* eslint-disable eqeqeq */
  if (arraysMatch(ratio, [1, 2])) {
    return "46vw";
  } else if (arraysMatch(ratio, [2, 3])) {
    return "40vw";
  } else if (arraysMatch(ratio, [3, 4])) {
    return "38vw";
  } else if (arraysMatch(ratio, [4, 3])) {
    return "28vw";
  } else if (arraysMatch(ratio, [3, 2])) {
    return "26vw";
  }
  // All ratios should be covered, but just in case
  return "35vw";
}

function generateMapListing(product) {
  return `
    <div class="map_listing ratio_${product.map_ratio[0]}_${product.map_ratio[1]}">
      <div class="map_image_container">
        <a href="/${product.path}">
          <img srcset="${generateSrcSetString(product.listing_images)}"
            sizes="${calculateSizes(product.map_ratio)}"
            src="${window.rambleMaps.imageUrl}${product.listing_images[0].name}"
            alt="Map of ${product.header}"
            loading="lazy">
        </a>
        <div class="map_listing_info">
          <a href="/${product.path}">
            <div class="map_listing_name">${product.header}</div>
          </a>
        </div>
      </div>
    </div>`;
}

// check the query to see if it matches a single entry in our list of autocomplete options
// (which includes individual maps, collections, locations, etc)
export function matchQueryToOptions(untrimmedQuery, entry) {
  const query = untrimmedQuery.replace(/\s+/g, ' ').trim();
  // if query includes a space, it is multiple words, so we want to see
  // if the query is included in the comparison string. Eg. Canyon National
  // Park should return Grand Canyon and Bryce Canyon
  if (query.includes(" ")) {
    return entry.toLowerCase().includes(query.toLowerCase());
  } else {
    // if we just have a single-word query, look to see if any of the maps
    // names contain a word that starts with that single-word query.
    // Eg. "Canyon" should hit for Bryce Canyon and Grand Canyon but
    // "ver" should not hit for "River"
    // Split search entrys by word and then match on the beginning of any of the words
    return entry.toLowerCase()
      .split(/\s/g)
      .find(word => word.startsWith(query.toLowerCase()));
  }
}

// check if the trimmed query matches the start of a term
function queryStartsWith(untrimmedQuery, entry) {
  const query = untrimmedQuery.replace(/\s+/g, ' ').trim();
  return entry.toLowerCase().startsWith(query.toLowerCase());
}

function sortResultsByQuery(untrimmedQuery) {
  return function sortOptions(a, b) {
    const firstEntry = a.header.toLowerCase();
    const secondEntry = b.header.toLowerCase();
    const query = untrimmedQuery.replace(/\s+/g, ' ').trim().toLowerCase();
    // if either the first or the second entry start with the string, bump
    // that entry to the front, otherwise, sort alphabetically
    if (firstEntry.startsWith(query) && !secondEntry.startsWith(query)) {
      return -1;
    } else if (secondEntry.startsWith(query) && !firstEntry.startsWith(query)) {
      return 1;
    }
    return firstEntry > secondEntry ? 1 : -1;
  }
}


function matchQueryToResults(userInput) {
  let matches = [];

  // a synonym will be an array of terms the query should match
  const synonym = window.rambleMaps.searchSynonyms[userInput.toLowerCase()] || false;

  // synonym is an array, if we have a synonym, it is our query, otherwise
  // we want to turn our user's input into an array so that it is the same
  // data type as our synonym and we can use the same methods to compare either
  const query = synonym || [userInput];

  // loop through all maps looking for matches. If we have a match, bail out of
  // the current iteration
  window.rambleMaps.products.forEach(map => {

    // if we have a synonym, find maps that start with the target term
    // and return location matches
    if (synonym) {
      const headerMatches = query.find((q) => queryStartsWith(q, map.header));
      if (headerMatches) {
        matches.push(map);
        return;
      }

      const locationMatches = query.find((q) => map.locations.find(loc => matchQueryToOptions(q, loc)));
      if (locationMatches) {
        matches.push(map);
        return;
      }

    // if we have a non synonym search, use our more inclusive results
    } else {
      const headerMatches = query.find((q) => matchQueryToOptions(q, map.header));
      if (headerMatches) {
        matches.push(map);
        return;
      }

      const locationMatches = query.find((q) => map.locations.find(loc => matchQueryToOptions(q, loc)));
      if (locationMatches) {
        matches.push(map);
        return;
      }

      const collectionMatches = query.find((q) => map.collections.find(collection => matchQueryToOptions(q, collection)));
      if (collectionMatches) {
        matches.push(map);
        return;
      }
    }
  });

  return matches;
}

// Return results that match the query, prioritizing maps whose name matches the header
// and then collection/location matches.
function generateSearchResults(query) {
  const maps = matchQueryToResults(query);

  // sort matches, prioritizing those whose header starts with the query and alphabetically
  // sorting the rest
  maps.sort(sortResultsByQuery(query));

  // if we have no matches, we want to:
  // 1) style the email capture to be our "no results" ui and
  // 2) show a 'browse all maps' link instead of clear all maps
  const gatherEmail = document.getElementById("js_search_gather_email");
  const clearResults = document.getElementById("js_clear_results");
  const browseAllMaps = document.getElementById("js_browse_all_maps");
  const searchResults = document.getElementById("js_map_search_results")
  const noResults = document.getElementById("js_search_no_results")

  if (!maps.length) {
    gatherEmail.classList.add("no_results");
    clearResults.classList.remove("show");
    browseAllMaps.classList.add("show");
    searchResults.classList.add("hide");
    noResults.classList.add("show")
  } else {
    gatherEmail.classList.remove("no_results");
    clearResults.classList.add("show");
    browseAllMaps.classList.remove("show");
    searchResults.classList.remove("hide");
    noResults.classList.remove("show")
  }

  return `
    <div class="listings_content">
      ${maps.map(generateMapListing).join("")}
    </div>`;
}

export function updateQueryInUrl(query) {
  if ('URLSearchParams' in window) { // doesn't work in IE
    const searchParams = new URLSearchParams(window.location.search);

    // clear existing query
    clearSearchParams(searchParams);

    searchParams.set("q", query);

    const newRelativePathQuery = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newRelativePathQuery);

    // fire a page view event so that GA can track our query via "Site Search"
    window.gtag('event', 'page_view', {
      page_title: 'Maps Search',
      page_location: window.location.href,
      page_path: `${window.location.pathname}${window.location.search}`
    });
  }
}

export function populateSearchResults(query) {
  const clearOrBrowseAnchor = document.getElementById("js_clear_or_browse");
  if (clearOrBrowseAnchor) {
   clearOrBrowseAnchor.classList.add("show");
  }

  const browseBy = document.getElementById("js_browse_by");
  if (browseBy) {
    browseBy.classList.add("hide");
  }

  // remove focus from search element if we've searched
  const input = document.getElementById("js_search_page_filter");
  if (input) {
    input.blur();
  }

  // if we have a maps search via the input, keep the input and show results
  const listingsContainer = document.getElementById("js_map_search_results");
  const mapListings = generateSearchResults(query);

  listingsContainer.innerHTML = mapListings;
}

export default function initSearchPage() {
  const input = document.getElementById("js_search_page_filter");

  function updateFromUrl() {
    // init page based on query parameters
    if ('URLSearchParams' in window) { // doesn't work in IE
      const searchParams = new URLSearchParams(window.location.search);
      const query = searchParams.get("q");
      const locationQuery = searchParams.get("location");
      const categoryQuery = searchParams.get("category");
      const stateQuery = searchParams.get("state");

      if (categoryQuery) {
        populateSearchResults(categoryQuery, "category");
      } else if (locationQuery) {
        populateSearchResults(locationQuery, "location");
      } else if (stateQuery) {
        populateSearchResults(stateQuery, "state");
      } else if (query) {
        input.value = query;
        populateSearchResults(query, "search");
      } else {
        clearResults(false);
      }
    }
  }

  if (input) {
    // init page based on query param contents
    updateFromUrl();

    // update query if URL changes via history popstate
    window.onpopstate = function handleHistoryChange() {
      updateFromUrl();
    };
  }
}
